import { navigate as intlSushishopNavigate } from "gatsby-plugin-intl-sushishop";
import { graphql, navigate as GatsbyNavigate, useStaticQuery } from "gatsby";
import { useCallback, useMemo } from "react";
import { useConfiguration } from "@src/hooks/query/useConfiguration";
import {
  GATSBY_CONF_HOME_DISPLAY_OTHER_CONTRY,
  GATSBY_SOURCE_SUSHISHOP_BASE_URL,
} from "@src/utils/constants";
import useBackboneContext from "@src/backbone/provider/useBackboneContext";
import { appConfig } from "@src/config/appConfig";
import hasWindow from "@src/utils/hasWindow";
import { useWebViewContext } from "@src/lib/WebView/WebViewContext";
import { isInternal } from "./navigationRules";

type NavigateType = typeof GatsbyNavigate;

// runtime RollingStart routing

export interface IRollingStartNavigation {
  navigate: NavigateType;
  goBack: () => void;
}

// Ré-export pour compat des imports existants (ex : LayoutProvider). La source
// de vérité est navigationRules.ts (partagé avec les composants Link).
export { isInternal } from "./navigationRules";

const useRollingStartNavigation = (): IRollingStartNavigation => {
  const { getConfiguration: c } = useConfiguration();
  const { allLanguage } =
    useStaticQuery<Queries.RollingStartNavigationQuery>(graphql`
      query RollingStartNavigation {
        allLanguage {
          nodes {
            name
            iso_code
            default_lang
            lang
          }
        }
      }
    `);

  const { isWebView } = useWebViewContext();
  const defaultLang: string = useMemo(
    () =>
      (
        allLanguage.nodes.find(({ default_lang }) => !!default_lang)
          ?.iso_code || "fr"
      ).toLowerCase(),
    [allLanguage.nodes]
  );

  // match controller name
  const getMatchedController = useCallback(
    (p: string): string | undefined => {
      // Return undefined for static files under /USER/ path
      if (p.startsWith("/USER/")) {
        return undefined;
      }

      const getLanguages = (): string[] => {
        const _list: string[] = [];
        allLanguage?.nodes?.forEach((langItem) => {
          const isoCode = langItem.iso_code?.toLowerCase() || "";
          if (isoCode) {
            _list.push(isoCode);
          }
        });
        return _list;
      };
      // language list
      const languages = getLanguages() || ["fr"];

      // widget page uri list
      // const allWidgetPageUri = new Set(
      //   allWidgetUri.nodes.map(({ uri }) => uri)
      // );
      // regex generator
      const generateRegExp = (
        mainLink: string,
        withLangPrefix: boolean = true
      ): RegExp => {
        // start with
        let reg = "^";
        if (withLangPrefix) {
          reg += `(/(${languages.join("|")})+)?`;
        }

        // main regex
        reg += mainLink;

        // end with
        reg += "/?$";
        const r = new RegExp(reg);
        return r;
      };
      const anyChar = "[a-z0-9-_]+";

      //   generate frontControllerMatch
      // const checkoutStepsRewrites = [
      //   c("_CART_BASE_URL_", "cart"),
      //   c("_CHECKOUT_CROSS_SELLING_REWRITE_", "cross-selling"),
      //   c("_CHECKOUT_PAYMENT_REWRITE_", "paiement"),
      // ].join("|");
      const frontControllerMatch: Record<string, (path: string) => boolean> = {
        Home: (p) => {
          // return new RegExp(`^/(${laguages.join("|")})?/?$`, "ig").test(p);
          return generateRegExp("/").test(p);
        },
        Category: (p) => {
          return (
            generateRegExp(`/${c("_CATEGORY_BASE_URL_", "")}/${anyChar}`).test(
              p
            ) ||
            generateRegExp(
              `/${c("_ALL_CATEGORIES_BASE_URL_", "all-categories")}`
            ).test(p)
          );
        },
        Product: (p) =>
          generateRegExp(
            `/(${c("_CATEGORY_BASE_URL_", "")})+/${anyChar}/${anyChar}-[0-9]+`
          ).test(p),
        // Store: (p) => {
        //   const storeLocUrl = c("_STORES_BASE_URL_", "restaurants");
        //   const storeBaseUrl = c("_STORE_BASE_URL_", "restaurant");
        //   const statePrefix = c("_STORELOC_STATE_PREFIX_", "r");
        //   const shopPrefix = c("_STORELOC_SHOP_PREFIX_", "s");
        //   const bounds = c("_STORELOC_BOUNDS_PREFIX_", "bounds");
        //   const pos = c("_STORELOC_POSITION_PREFIX_", "pos");
        //   const reRoot = generateRegExp(`/${storeLocUrl}/?$`);
        //   const reState = generateRegExp(
        //     `/${storeLocUrl}/(${statePrefix})/${anyChar}`
        //   );
        //   const reCity = generateRegExp(`/${storeLocUrl}/${anyChar}`);
        //   const rePostcode = generateRegExp(
        //     `/${storeLocUrl}/${anyChar}/${anyChar}`
        //   );
        //   const reShop = generateRegExp(
        //     `/${storeLocUrl}/${shopPrefix}/${anyChar}`
        //   );
        //   const reStore = generateRegExp(`/${storeBaseUrl}/${anyChar}`);
        //   const reBoundsOrPos = generateRegExp(
        //     `/${storeLocUrl}/(${bounds}|${pos})/.+`
        //   );
        //   const success =
        //     reRoot.test(p) || // root
        //     reState.test(p) || // state
        //     reCity.test(p) || // city
        //     rePostcode.test(p) || // postcode
        //     reShop.test(p) || // shop
        //     reStore.test(p) || // boutique
        //     reBoundsOrPos.test(p);
        //   return success;
        // },
        // Account: (p) =>
        //   generateRegExp(
        //     `/(${c("_ACCOUNT_BASE_URL_", "")})/?([a-z0-9-]+)?`
        //   ).test(p) ||
        //   generateRegExp(`/(${c("_LOGOUT_REWRITE_", "")})/?([a-z0-9-]+)?`).test(
        //     p
        //   ),
        // Authentification: (p) => {
        //   const authBaseUrl = c("_AUTHENTIFICATION_BASE_URL_", "");
        //   const resetPassword = c(
        //     "_AUTHENTIFICATION_RESET_PASSWORD_BASE_URL_",
        //     "reset"
        //   );
        //   const authMySushiShopRewrite = c(
        //     "_MY_SUSHI_SHOP_AUTH_REWRITE_",
        //     "welcome"
        //   );
        //   const authBaseUrlRegex = generateRegExp(
        //     `/${authBaseUrl}/?([a-z0-9-]+)?`
        //   );
        //   const mySushiShopAuthRegex = generateRegExp(
        //     `/${authBaseUrl}/${authMySushiShopRewrite}/?([a-z0-9-]+)?`
        //   );
        //   const resetPasswordTest = generateRegExp(
        //     `/${authBaseUrl}/${resetPassword}.*`
        //   );
        //   return (
        //     resetPasswordTest.test(p) ||
        //     authBaseUrlRegex.test(p) ||
        //     mySushiShopAuthRegex.test(p)
        //   );
        // },
        // Reorder: (p) =>
        //   generateRegExp(`/(${c("_PAGE_REORDER_BASE_URL_", "")})`).test(p),
        // BlankTest: (p) => generateRegExp(`/blank-test`).test(p),
        // CheckoutOpc: (p) =>
        //   generateRegExp(
        //     `/(${c(
        //       "_CHECKOUT_BASE_URL_",
        //       "checkout"
        //     )})\/(${checkoutStepsRewrites})?`
        //   ).test(p),
        // CheckoutConfirmation: (p) =>
        //   generateRegExp(
        //     `/(${c("_CHECKOUT_BASE_URL_", "checkout")})/confirmation`
        //   ).test(p),
        // CheckoutPending: (p) =>
        //   generateRegExp(
        //     `/(${c("_CHECKOUT_BASE_URL_", "checkout")})/pending`
        //   ).test(p),
        // CheckoutError: (p) =>
        //   generateRegExp(
        //     `/(${c("_CHECKOUT_BASE_URL_", "checkout")})/error`
        //   ).test(p),
        // SupportCenter: (p) =>
        //   generateRegExp(
        //     `/(${c(
        //       "_SUPPORT_CENTER_BASE_URL_",
        //       "service-client"
        //     )})/?(([a-z]+)-([0-9]+)|[a-z0-9-]*)`
        //   ).test(p),
        // News: (p) =>
        //   generateRegExp(`/(${c("_PAGE_NEWS_BASE_URL_", "actualite")})`).test(
        //     p
        //   ) ||
        //   generateRegExp(
        //     `/(${c("_PAGE_NEWS_BASE_URL_", "actualite")})/(flag)/([a-z0-9-]+)`
        //   ).test(p) ||
        //   generateRegExp(
        //     `/(${c("_PAGE_NEWS_BASE_URL_", "actualite")})/([a-z0-9-]+)`
        //   ).test(p),
        // Widget: (p) =>
        //   p.endsWith("/")
        //     ? allWidgetPageUri.has(p.slice(0, -1)) || allWidgetPageUri.has(p)
        //     : allWidgetPageUri.has(p + "/") || allWidgetPageUri.has(p),
        // Discount: (p) => {
        //   return generateRegExp(
        //     `/(${c("_DISCOUNT_PAGE_BASE_URL_", "discount")})/([a-z0-9-]+)`
        //   ).test(p);
        // },
      };

      return (
        Object.keys(frontControllerMatch).find((k) =>
          frontControllerMatch[k](p)
        ) || "Error"
      );
    },
    [allLanguage?.nodes, c]
  );

  const navigate: NavigateType = useCallback(
    (_to, options: any = undefined) => {
      if (typeof _to === "number" || !_to) {
        return intlSushishopNavigate(_to, options);
      }
      // Ne pas préfixer la locale sur une URL absolue (http(s)://) :
      // sinon un lien absolu deviendrait "/es/https://..." → 404 (bug RUN-7752).
      // Le replace de la base URL ci-dessous convertit ensuite les liens internes absolus
      // en chemins relatifs, et les vrais liens externes restent gérés par isExternal.
      const isAbsoluteUrl = /^https?:\/\//i.test(_to);
      let to =
        GATSBY_CONF_HOME_DISPLAY_OTHER_CONTRY && !isAbsoluteUrl
          ? _to.startsWith(`/${defaultLang}`)
            ? _to
            : `/${defaultLang}${_to.startsWith("/") ? _to : "/" + _to}`
          : _to;
      const re = new RegExp(`^${GATSBY_SOURCE_SUSHISHOP_BASE_URL}`, "i");
      to = to.replace(re, "");

      // Choose navigation type
      if (isInternal(to)) {
        if (options && options.scrollNavigation) {
          return GatsbyNavigate(to, options);
        } else {
          return intlSushishopNavigate(to, options);
        }
      } else {
        if (hasWindow()) {
          const isExternal = to.startsWith("http");
          if (isWebView && !isExternal) {
            // gestion du routeur deeplink dans les LP
            // car elles ne gèrent pas bien l'UniversalLink dans une webview
            const controller = getMatchedController(to);
            const url_scheme = "sushishop://";
            let deeplink = "";
            switch (controller) {
              case "Home":
                deeplink = url_scheme + "default";
                break;
              case "Category":
                const link_rewrite = to
                  .replace(/^\/+|\/+$/g, "")
                  .split("/")
                  .pop();
                deeplink = url_scheme + "category?link_rewrite=" + link_rewrite;
                break;
              case "Product":
                const id_product = to.replace(/.*-(\d+)$/, "$1");
                deeplink = url_scheme + "product?id_product=" + id_product;
                break;
            }
            if (deeplink) {
              window.location.href = deeplink;
            }
          } else {
            window.location.href = isExternal
              ? to
              : GATSBY_SOURCE_SUSHISHOP_BASE_URL + to;
          }
        }
      }
      return Promise.resolve();
    },
    [defaultLang, isWebView]
  );

  return {
    navigate,
    goBack: () => GatsbyNavigate(-1),
  };
};

export default useRollingStartNavigation;
